import { BatteryIcon } from "../../../Assets/icons/BatteryIcon";
import { PowerIcon } from "../../../Assets/icons/PowerIcon";
import logo from "../../../Assets/images/logo.png";
import flashLense from "../../../Assets/images/V2_Image.png";
import campassImg from "../../../Assets/images/campass-frame.svg";
import sunImg from "../../../Assets/images/sun-image.svg";
import { useEffect, useState, useRef } from "react";
import moment from "moment";
import { EnergyChart, MapLayers, ToggleStyleControl } from "../../../components";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import mapboxgl from "mapbox-gl";
import { DropdownArrowIcon } from "../../../Assets/icons/DropdownArrowIcon";
import { authService } from "../../../services/authServices";
import { Month } from "../../../utils/constant";
import { toast } from "react-toastify";
import { unitService } from "../../../services/unitServices";

const URL = process.env.REACT_APP_SOCKET_URL;
const ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
mapboxgl.accessToken = ACCESS_TOKEN;

export const UnitDetailPage = (props) => {
  const { id } = useParams();
  const [solarData, setSolarData] = useState({});
  const [unitData, setUnitData] = useState({});
  const [graphData, setGraphData] = useState({});
  const [azimuth, setAzimuth] = useState(solarData?.azimuth);
  const [timezone, setTimezone] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [is3D, setIs3D] = useState(false);
  const [baseStyle, setBaseStyle] = useState(
    localStorage.getItem("baseStyle") || "satellite-streets"
  );
  const mapStyle = `mapbox://styles/mapbox/${baseStyle}-${is3D
    ? baseStyle === "dark" || baseStyle === "light"
      ? "v11"
      : "v12"
    : "v9"
    }`;

  console.log("data", solarData, graphData);
  const date = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  const timestamp =
    date.getFullYear().toString() +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    pad(date.getSeconds());

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz);
  }, []);

  useEffect(() => {
    if (!id || !timezone) return;
    let intervalId;

    setGraphData(null);
    setSolarData(null);
    setLoading(true);

    const socket = new WebSocket(`${URL}/ws/data/${id}/`);

    socket.onopen = () => {
      let msgData;
      if (selectedMonth !== null) {
        msgData = {
          event: "get_data",
          timestamp: timestamp,
          message: "Hello",
          tz_name: timezone,
          month_range: selectedMonth,
        };
      } else {
        msgData = {
          event: "get_data",
          timestamp: timestamp,
          message: "Hello",
          tz_name: timezone,
        };
      }
      socket.send(JSON.stringify(msgData));
      intervalId = setInterval(() => {
        socket.send(JSON.stringify(msgData));
      }, 30000);
    };

    socket.onerror = () => {
      setLoading(false);
    };

    socket.onmessage = (event) => {
      try {
        const data = event.data;
        const parsedData = JSON.parse(data);
        setSolarData(parsedData.solar_data);
        setGraphData(parsedData.graph_data);
        setLoading(false);
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };

    return () => {
      socket.close(); // Clean up on unmount or id change
      clearInterval(intervalId); // Clean up interval
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, timezone, selectedMonth]);


  function getTimezoneOffsetMinutes(tzString) {
    const match = tzString.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!match) return 0; // default UTC
    const sign = match[1] === "+" ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes);
  }


  const updatedTime = solarData?.time;
  const apiTimezone = solarData?.unit_timezone || "GMT+00:00";

  const offsetMinutes = getTimezoneOffsetMinutes(apiTimezone);

  // STEP 1️⃣  Parse backend time as local (in its timezone) and convert to UTC
  const backendTimeInUTC = updatedTime
    ? moment(updatedTime, "YYYYMMDDHHmmss").utcOffset(offsetMinutes, true).utc()
    : null;

  // STEP 2️⃣  Get current time in the same timezone (from UTC)
  const currentTimeInApiTZ = moment.utc().utcOffset(offsetMinutes);


  // STEP 3️⃣  Compare both (same frame of reference)
  const lastUpdatedAt = backendTimeInUTC
    ? backendTimeInUTC.from(currentTimeInApiTZ)
    : "";
  const formattedDate = updatedTime
    ? moment(updatedTime, "YYYYMMDDHHmmss").format("DD/MMM/YYYY")
    : "";
  const formattedTime = updatedTime
    ? moment(updatedTime, "YYYYMMDDHHmmss").format("HH:mm:ss")
    : "";

  useEffect(() => {
    if (!solarData) {
      setLoading(true);
    } else {
      setLoading(false);
      setAzimuth(solarData?.azimuth);
    }
  }, [solarData]);

  const currentTime = moment(updatedTime, "YYYYMMDDHHmmss").format(
    "DD/MMM/YYYY HH:mm:ss"
  );

  const currentIrradiance =
    solarData?.dni && solarData?.elev
      ? Math.max(0, solarData.dni * (solarData.elev / 90))
      : null;

  const currentPower = currentIrradiance
    ? currentIrradiance * 0.2 * 0.15
    : null;

  const currentFinalPower = currentPower ? Math.min(currentPower, 30) : null;

  const monthlyIrradiance =
    solarData?.monthly_dni && solarData?.elev
      ? Math.max(0, solarData.monthly_dni * (solarData.elev / 90))
      : null;

  const monthlyPower = monthlyIrradiance
    ? monthlyIrradiance * 0.2 * 0.15
    : null;

  const monthlyFinalPower = monthlyPower ? Math.min(monthlyPower, 30) : null;

  const handleToggleMap = () => {
    setIs3D((prev) => {
      const newVal = !prev;
      localStorage.setItem("map_projection", newVal ? true : false);
      return newVal;
    });
  };

  useEffect(() => {
    if (!unitData?.lat || !unitData?.lon) return;
    if (!mapRef.current) return;

    mapInstance.current = new mapboxgl.Map({
      container: mapRef.current,
      style: mapStyle,
      center: [Number(unitData.lon), Number(unitData.lat)],
      zoom: 4,
      telemetry: false,
      attributionControl: false,
    });
    mapInstance.current.addControl(
      MapLayers(setBaseStyle, baseStyle),
      "bottom-right"
    );
    const toggleControl = new ToggleStyleControl(handleToggleMap);
    mapInstance.current.addControl(toggleControl, "bottom-right");

    new mapboxgl.Marker({ color: "#ffe79b" })
      .setLngLat([Number(unitData.lon), Number(unitData.lat)])
      .addTo(mapInstance.current).getElement().style.cursor = "pointer";

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [unitData]);

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setStyle(mapStyle);
    }
  }, [mapStyle]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, unitsData] = await Promise.all([
          authService.getUserProfile(),
          unitService.getUnitDetailsById(id),
        ]);

        if (profileData.code === 200) {
          setUserProfile(profileData.data);
        }
        if (unitsData.code === 200) {
          setUnitData(unitsData.data);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message?.[0]?.detail;
        const code = error.response?.data?.code || error.response?.data?.status;
        if (code !== 401) {
          toast.error(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => {
    const savedMonth = localStorage.getItem(`selectedMonth_${id}`);
    if (savedMonth) {
      setSelectedMonth(Number(savedMonth));
    }

    // Cleanup on unmount: remove localStorage key
    return () => {
      localStorage.removeItem(`selectedMonth_${id}`);
    };
  }, [id]);

  const handleSelect = (month) => {
    setSelectedMonth(month);
    if (month !== null) {
      localStorage.setItem(`selectedMonth_${id}`, month);
    } else {
      localStorage.removeItem(`selectedMonth_${id}`);
    }
    setIsOpen(false);
  };

  return (
    <>
      <div className="main min-vh-100 d-flex justify-content-center flex-row w-100">
        <div className="page-wrapper primary-bg px-sm-4 px-3 py-xl-5 py-sm-4 py-3 w-100">
          <header className=" top-0 primary-bg z-index-99">
            <div className="header-wrapper position-relative">
              <div className="logo-wrapper">
                <Link to="/" className="position-relative z-index-99">
                  <img
                    className="logo"
                    src={logo}
                    width={239}
                    height={72}
                    alt="logo"
                  />
                </Link>
              </div>
              <div className="time-wrapper d-flex align-items-center flex-column gap-sm-2 gap-1 position-absolute top-0 bottom-0 justify-content-center">
                <div className="time-div text-center">
                  <h5 className="white-font font-we-sb d-inline-block fw-normal">
                    Last message received
                  </h5>
                </div>
                <div className="d-flex align-items-center gap-sm-3 gap-2">
                  <div className="date-div text-center">
                    <h3 className="op-5 white-font mb-2 d-inline-block fw-normal">
                      Date
                    </h3>
                    {!formattedDate ? (
                      <h1 className="primary-font font-we-sb">
                        <Skeleton
                          width={100}
                          baseColor="#1b1b1b"
                          highlightColor="#444444ff"
                        />
                      </h1>
                    ) : (
                      <h1 className="white-font font-we-sb">{formattedDate}</h1>
                    )}
                  </div>
                  <div className="time-div text-center">
                    <h3 className="op-5 white-font mb-2 d-inline-block fw-normal">
                      Time
                    </h3>
                    {!formattedTime ? (
                      <h1 className="primary-font font-we-sb">
                        <Skeleton
                          width={100}
                          baseColor="#1b1b1b"
                          highlightColor="#444444ff"
                        />
                      </h1>
                    ) : (
                      <h1 className="primary-font font-we-sb">
                        {formattedTime}
                      </h1>
                    )}
                  </div>
                  <div className="time-div text-center">
                    <h3 className="op-5 white-font mb-2 d-inline-block fw-normal">
                      Timezone
                    </h3>
                    {!solarData?.unit_timezone ? (
                      <h1 className="primary-font font-we-sb">
                        <Skeleton
                          width={100}
                          baseColor="#1b1b1b"
                          highlightColor="#444444ff"
                        />
                      </h1>
                    ) : (
                      <h1 className="white-font font-we-sb">
                        {solarData?.unit_timezone}
                      </h1>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </header>
          <div className="content">
            <div className="d-flex flex-column justify-content-between gap-xl-4 gap-3">
              <div className="row inner-content1 gap-lg-0 gap-3">
                {loading ? (
                  <div className="col-lg-4 col-12">
                    <Skeleton
                      height={300}
                      baseColor="#1b1b1b"
                      highlightColor="#444444ff"
                    />
                  </div>
                ) : (
                  <div className="col-lg-4 col-12">
                    <div className="secondary-bg custom-rounded card h-100">
                      <h1 className="white-font font-we-nr  pb-2 pt-xl-2">
                        <span className="op-5">Solar Unit ID:</span>{" "}
                        {solarData?.unit_name}
                      </h1>
                      <h5 className="white-font op-5 font-we-nr">
                        {`Updated ${lastUpdatedAt}`}
                      </h5>
                      <div className="row align-items-center justify-content-between d-flex card1">
                        <div className="col-xl-7 col-sm-8 col-12">
                          <div className="row g-sm-3 g-2 mt-1">
                            <div className="col-12">
                              <div className="primary-bg-shade rounded py-xl-4 py-3 px-xl-4 px-3 d-flex align-items-center gap-3 ">
                                <h4 className="white-font fw-normal op-5">
                                  Status:
                                </h4>
                                <h1 className="white-font font-we-sb">
                                  {solarData?.status ? solarData?.status : "--"}
                                </h1>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="primary-bg-shade rounded py-xl-2 py-3 ps-xl-4 ps-3 pe-4 position-relative">
                                <div className="secondary-bg icon-wrapper rounded-circle d-flex align-items-center justify-content-center position-absolute ">
                                  <span className="primary-font">
                                    <BatteryIcon />
                                  </span>
                                </div>
                                <div className="py-2">
                                  <h4 className="white-font fw-normal op-5 mb-3">
                                    Battery Voltage
                                  </h4>
                                  <h1 className="white-font font-we-sb">
                                    {solarData?.batt_volt
                                      ? `${solarData?.batt_volt} Volt`
                                      : "--"}
                                    {/* {`${solarData?.batt_volt} Volt`} */}
                                  </h1>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="primary-bg-shade rounded py-xl-2 py-3 ps-xl-4 ps-3 pe-4 position-relative">
                                <div className="secondary-bg icon-wrapper rounded-circle d-flex align-items-center justify-content-center position-absolute ">
                                  <span className="primary-font">
                                    <PowerIcon />
                                  </span>
                                </div>
                                <div className="py-2">
                                  <h4 className="white-font fw-normal op-5 mb-3">
                                    Power
                                  </h4>
                                  <h1 className="white-font font-we-sb">
                                    {solarData?.output
                                      ? `${solarData?.output} Watt`
                                      : "--"}
                                    {/* {`${solarData?.output} Watt`} */}
                                  </h1>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-5 col-sm-4 col-12 text-center">
                          <img
                            src={flashLense}
                            alt="flash-lense"
                            width={250}
                            className="flash-lense-img"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {loading || !graphData ? (
                  <div className="col-lg-8 col-12">
                    <Skeleton
                      className="custom-rounded card d-flex flex-column justify-content-between h-100"
                      baseColor="#1b1b1b"
                      highlightColor="#444444ff"
                      borderRadius="12px"
                    />
                  </div>
                ) : (
                  <div className="col-lg-8 col-12">
                    <div className="secondary-bg custom-rounded card d-flex flex-column justify-content-between h-100">
                      <div className="d-flex flex-row justify-content-between">
                        <div>
                          <h2 className="white-font font-we-nr pb-2">
                            Energy Generated{" "}
                            <span className="fw-light">Wh</span>
                          </h2>
                          <h5 className="white-font op-5 font-we-nr">
                            {selectedMonth
                              ? `15 ${
                                  Month.find((m) => m.value == selectedMonth)
                                    ?.name
                                }`
                              : `Updated ${lastUpdatedAt}`}
                          </h5>
                        </div>

                        <div className="dropdown">
                          <button
                            className="btn btn-secondary secondary-bg white-font dropdown-toggle py-2 px-4"
                            type="button"
                            onClick={() => setIsOpen((prev) => !prev)}
                          >
                            {selectedMonth !== null
                              ? Month.find((m) => m.value == selectedMonth)
                                  ?.name
                              : "Select Month Range"}
                          </button>
                          {isOpen && (
                            <ul
                              className="dropdown-menu show position-absolute mt-1 secondary-bg white-font dropdown-menu-end right-0 end-0"
                              style={{ minWidth: "100%" }}
                            >
                              <li>
                                <button
                                  className="dropdown-item secondary-bg white-font"
                                  onClick={() => handleSelect(null)}
                                >
                                  Select Month Range
                                </button>
                              </li>
                              {Month.map((month) => (
                                <li key={month.id}>
                                  <div
                                    className="dropdown-item cursor-pointer secondary-bg white-font"
                                    onClick={() => handleSelect(month.value)}
                                  >
                                    {month.name}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      <div className="mb-lg-0 d-flex">
                        <div className="barchart">
                          {graphData && <EnergyChart graphData={graphData} />}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row inner-content2 gap-lg-0 gap-3">
                {loading ? (
                  <div className="col-lg-4 col-12">
                    <Skeleton
                      height={300}
                      baseColor="#1b1b1b"
                      highlightColor="#444444ff"
                    />
                  </div>
                ) : (
                  <div className="col-lg-4 col-12">
                    <div className="secondary-bg custom-rounded p-xl-4 p-3 h-100">
                      <div className="d-flex justify-content-between flex-sm-row flex-column gap-sm-0 gap-3">
                        <div>
                          <h2 className="white-font font-we-nr pb-2">
                            Sun’s Position
                          </h2>
                          <h5 className="white-font op-5 font-we-nr">
                            {`Updated ${lastUpdatedAt}`}
                          </h5>
                        </div>
                        <div className="d-flex  gap-xl-3 gap-2">
                          <div className="primary-bg-shade py-2 px-3 d-flex align-items-center gap-2 rounded">
                            <h4 className="white-font font-we-nr op-5">
                              Azimuth
                            </h4>
                            <h3 className="white-font font-we-nr">
                              {solarData?.azimuth ? solarData?.azimuth : "--"}
                            </h3>
                          </div>
                          <div className="primary-bg-shade py-2 px-3 d-flex align-items-center gap-2 rounded">
                            <h4 className="white-font font-we-nr op-5">Elev</h4>

                            <h3 className="white-font font-we-nr angle-set">
                              {solarData?.elev ? solarData?.elev : "--"}{" "}
                              <sup></sup>
                            </h3>
                          </div>
                        </div>
                      </div>
                      <div className="campass-img d-flex mx-auto justify-content-center my-lg-0 my-3 position-relative">
                        <div
                          style={{
                            transform: `rotate(${azimuth}deg)`,
                          }}
                        >
                          <h5
                            className="text-white angle-set position-absolute start-0 end-0 mx-auto text-center"
                            style={{
                              transform: `rotate(-${azimuth}deg)`,
                            }}
                          >
                            Elev {Math.round(solarData?.elev)} <sup></sup>
                          </h5>
                        </div>
                        <div
                          style={{
                            transform: `rotate(${azimuth}deg)`,
                          }}
                        >
                          <img
                            src={sunImg}
                            alt="sun-images"
                            className="sun-image"
                          />
                        </div>
                        <img
                          src={campassImg}
                          alt="campass-img"
                          style={{ padding: "15px" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {loading ? (
                  <div className="col-lg-8 col-12">
                    <Skeleton
                      height={300}
                      baseColor="#1b1b1b"
                      highlightColor="#444444ff"
                    />
                  </div>
                ) : (
                  <div className="col-lg-8 col-12">
                    <div className="secondary-bg custom-rounded p-xl-4 p-3 h-100">
                      <div className="d-flex justify-content-between flex-sm-row flex-column gap-sm-0 gap-3 align-items-sm-center align-items-start">
                        <div>
                          <h2 className="white-font font-we-nr">
                            Solar Unit Position
                          </h2>
                          <h5 className="white-font op-5 font-we-nr">
                            {`Updated ${lastUpdatedAt}`}
                          </h5>
                        </div>
                        <div className="d-flex gap-xl-3 gap-2">
                          <div className="primary-bg-shade py-2 px-3 d-flex align-items-center gap-2 rounded">
                            <h4 className="white-font font-we-nr op-5">LAT</h4>
                            <h3 className="white-font font-we-nr">
                              {unitData?.lat
                                ? parseFloat(unitData?.lat).toFixed(1)
                                : "--"}
                            </h3>
                          </div>
                          <div className="primary-bg-shade py-2 px-3 d-flex align-items-center gap-2 rounded">
                            <h4 className="white-font font-we-nr op-5">LON</h4>
                            <h3 className="white-font font-we-nr">
                              {unitData?.lon
                                ? parseFloat(unitData?.lon).toFixed(1)
                                : "--"}
                            </h3>
                          </div>
                        </div>
                      </div>

                      <div className="text-center pt-3 rounded-3 map-view">
                        {unitData?.lat && unitData?.lon && (
                          <div ref={mapRef} style={{ height: "95%" }} />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row inner-content3 gap-lg-0 gap-3 mt-xl-5 mt-3">
                {(userProfile?.user_role === "SUPER_ADMIN" ||
                  userProfile?.email
                    ?.split("@")[1]
                    ?.includes("iris-solar.com")) &&
                  (loading || !solarData ? (
                    <div className="col-lg-12 col-12">
                      <Skeleton
                        height={200}
                        baseColor="#1b1b1b"
                        highlightColor="#444444ff"
                      />
                    </div>
                  ) : (
                    <div className="col-lg-12 col-12">
                      <div className="secondary-bg custom-rounded card h-100">
                        <div
                          className="d-flex justify-content-between align-items-center cursor-pointer"
                          onClick={() => setIsDebugOpen(!isDebugOpen)}
                        >
                          <div>
                            <h1 className="white-font font-we-nr pb-2">
                              Debug
                            </h1>
                            <h5 className="white-font op-5 font-we-nr pb-4">
                              {`Updated ${lastUpdatedAt}`}
                            </h5>
                          </div>
                          <div
                            className="cursor-pointer"
                            onClick={() => setIsDebugOpen(!isDebugOpen)}
                            style={{
                              transform: isDebugOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          >
                            <DropdownArrowIcon />
                          </div>
                        </div>
                        {isDebugOpen && (
                          <div className="debug-content">
                            <div className="white-font font-we-sb mb-4">
                              <span className="op-5">Lat: </span>
                              {solarData?.lat
                                ? parseFloat(solarData?.lat).toFixed(1)
                                : "--"}
                              <span className="op-5 ms-3">Lon: </span>
                              {solarData?.lon
                                ? parseFloat(solarData?.lon).toFixed(1)
                                : "--"}
                            </div>

                            <div className="row gap-lg-0 gap-3">
                              <div className="col-lg-4 col-12">
                                <div className="primary-bg-shade rounded py-3 px-3 h-100">
                                  <h3 className="white-font font-we-sb mb-3">
                                    Energy Generated (Hourly)
                                  </h3>
                                  <h4 className="primary-font font-we-sb mb-3">
                                    Energy Calculation
                                  </h4>

                                  <div className="secondary-bg rounded py-3 px-3 mb-3">
                                    <code className="white-font op-7">
                                      Energy (Wh) = Power (W) × Time (1 hour)
                                    </code>
                                  </div>

                                  <div className="white-font font-we-sb fw-normal">
                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div className="d-flex flex-column gap-2">
                                      <div className="d-flex justify-content-between">
                                        <span>Current Time:</span>
                                        <span className="secondary-font fw-bold">
                                          {formattedTime}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Current Power:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.output
                                            ? `${solarData?.output}`
                                            : "--"}{" "}
                                          W
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Time Period:</span>
                                        <span className="secondary-font fw-bold">
                                          1 hour
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div>
                                      <code className="primary-font">
                                        Current Energy:{" "}
                                        {solarData?.output
                                          ? `${solarData?.output} × 1 = ${solarData?.output}`
                                          : "--"}{" "}
                                        Wh
                                      </code>
                                    </div>
                                    <div>
                                      <code className="primary-font">
                                        Adjusted Energy:{" "}
                                        {monthlyFinalPower
                                          ? `${monthlyFinalPower.toFixed(
                                              2
                                            )} × 1 = ${monthlyFinalPower.toFixed(
                                              2
                                            )}`
                                          : "--"}{" "}
                                        Wh
                                      </code>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-lg-4 col-12">
                                <div className="primary-bg-shade rounded py-3 px-3 h-100">
                                  <h4 className="primary-font font-we-sb mb-3">
                                    Power Calculation
                                  </h4>

                                  <div className="d-flex flex-column gap-2 mb-3">
                                    <div className="secondary-bg rounded py-3 px-3">
                                      <code className="white-font op-7">
                                        Irradiance = max(0, DNI x (Solar
                                        Elevation / 90))
                                      </code>
                                    </div>
                                    <div className="secondary-bg rounded py-3 px-3">
                                      <code className="white-font op-7">
                                        Power = Irradiance x Panel Area x
                                        Efficiency
                                      </code>
                                    </div>
                                    <div className="secondary-bg rounded py-3 px-3">
                                      <code className="white-font op-7">
                                        Final = min(Power, Max Watt)
                                      </code>
                                    </div>
                                  </div>

                                  <div className="white-font font-we-sb fw-normal">
                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div className="d-flex flex-column gap-2">
                                      <div className="d-flex justify-content-between">
                                        <span>DNI:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.dni ?? "--"} kWh/m²
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Solar Elevation:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.elev ?? "--"}°
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Panel Area:</span>
                                        <span className="secondary-font fw-bold">
                                          0.2 m²
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Panel Efficiency:</span>
                                        <span className="secondary-font fw-bold">
                                          15%
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div className="d-flex flex-column gap-1">
                                      <div>
                                        <code className="primary-font">
                                          Irradiance:{" "}
                                          {currentIrradiance
                                            ? `max(0, ${solarData?.dni} × (${
                                                solarData?.elev
                                              }/90)) = ${currentIrradiance.toFixed(
                                                1
                                              )} W/m²`
                                            : "--"}
                                        </code>
                                      </div>
                                      <div>
                                        <code className="primary-font">
                                          Power:{" "}
                                          {currentPower
                                            ? `${currentIrradiance.toFixed(
                                                1
                                              )} × 0.2 × 0.15 = ${currentPower.toFixed(
                                                2
                                              )} W`
                                            : "--"}
                                        </code>
                                      </div>
                                      <div>
                                        <code className="primary-font">
                                          Final:{" "}
                                          {currentFinalPower
                                            ? `min(${currentPower.toFixed(
                                                2
                                              )}, 30) = ${currentFinalPower.toFixed(
                                                2
                                              )} W`
                                            : "--"}
                                        </code>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-lg-4 col-12">
                                <div className="primary-bg-shade rounded py-3 px-3 h-100">
                                  <h3 className="white-font font-we-sb mb-3">
                                    DNI Calculations (Monthly)
                                  </h3>
                                  <h4 className="primary-font font-we-sb mb-3">
                                    Monthly Calculations
                                  </h4>

                                  <div className="secondary-bg rounded py-3 px-3 mb-3">
                                    <code className="white-font op-7">
                                      Monthly DNI = Base DNI x Seasonal Factor
                                    </code>
                                  </div>

                                  <div className="white-font font-we-sb fw-normal">
                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div className="d-flex flex-column gap-2">
                                      <div className="d-flex justify-content-between">
                                        <span>Month:</span>
                                        <span className="secondary-font fw-bold">
                                          {Month.find(
                                            (m) => m.value == solarData?.month
                                          )?.name ?? "--"}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Base DNI:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.dni ?? "--"} kWh/m²
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>ClearSky DNI:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.clear_sky
                                            ? parseFloat(
                                                solarData.clear_sky
                                              ).toFixed(1)
                                            : "--"}{" "}
                                          kWh/m²
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Seasonal Factor:</span>
                                        <span className="secondary-font fw-bold">
                                          {solarData?.seasonal_factor
                                            ? solarData.seasonal_factor.toFixed(
                                                3
                                              )
                                            : "--"}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border-top border-secondary opacity-25 my-3"></div>

                                    <div className="d-flex flex-column gap-1">
                                      <div>
                                        <code className="primary-font fw-normal">
                                          Monthly DNI:{" "}
                                          {solarData?.monthly_dni &&
                                          solarData?.dni
                                            ? ` ${solarData.dni.toFixed(
                                                1
                                              )} × ${(
                                                solarData.monthly_dni /
                                                solarData.dni
                                              ).toFixed(
                                                3
                                              )} = ${solarData.monthly_dni.toFixed(
                                                1
                                              )} W/m²`
                                            : "--"}
                                        </code>
                                      </div>

                                      <div className="d-flex flex-column gap-1">
                                        <div>
                                          <code className="primary-font">
                                            Monthly DNI:{" "}
                                            {solarData?.monthly_dni
                                              ? `min(${solarData.monthly_dni.toFixed(
                                                  1
                                                )}, 1200.0) = ${Math.min(
                                                  solarData.monthly_dni,
                                                  1200
                                                ).toFixed(1)} W/m²`
                                              : "--"}
                                          </code>
                                        </div>

                                        {solarData?.month_elev ? (
                                          <div>
                                            {/* Step 2: Irradiance Calculation */}
                                            <div>
                                              <code className="primary-font">
                                                Irradiance:{" "}
                                                {(() => {
                                                  const elev = parseFloat(
                                                    solarData.month_elev
                                                  );
                                                  const monthlyDniRaw =
                                                    parseFloat(solarData.dni) *
                                                    parseFloat(
                                                      solarData.seasonal_factor
                                                    );
                                                  const adjustedDni = Math.min(
                                                    monthlyDniRaw,
                                                    1200.0
                                                  );

                                                  if (isNaN(elev) || elev < 2) {
                                                    return `Elevation ${
                                                      isNaN(elev)
                                                        ? "NaN"
                                                        : elev.toFixed(2)
                                                    }° < 2°, so Irradiance = 0 W/m²`;
                                                  }

                                                  if (elev < 5) {
                                                    const factor =
                                                      (elev - 2) / 3;
                                                    const irradiance =
                                                      adjustedDni *
                                                      Math.sin(
                                                        (elev * Math.PI) / 180
                                                      ) *
                                                      factor;
                                                    return `${adjustedDni.toFixed(
                                                      1
                                                    )} × sin(${elev.toFixed(
                                                      2
                                                    )}°) × ((${elev.toFixed(
                                                      2
                                                    )} - 2) / 3) = ${irradiance.toFixed(
                                                      2
                                                    )} W/m²`;
                                                  }

                                                  const irradiance =
                                                    adjustedDni *
                                                    Math.sin(
                                                      (elev * Math.PI) / 180
                                                    );
                                                  return `${adjustedDni.toFixed(
                                                    1
                                                  )} × sin(${elev.toFixed(
                                                    2
                                                  )}°) = ${irradiance.toFixed(
                                                    2
                                                  )} W/m²`;
                                                })()}
                                              </code>
                                            </div>

                                            {/* Step 3: Power Calculation */}
                                            <div>
                                              <code className="primary-font">
                                                Power:{" "}
                                                {(() => {
                                                  const elev = parseFloat(
                                                    solarData.month_elev
                                                  );
                                                  const adjustedDni = Math.min(
                                                    solarData.monthly_dni,
                                                    1200.0
                                                  );
                                                  const panelArea = 0.2;
                                                  const efficiency = 0.15;

                                                  let irradiance = 0;
                                                  if (isNaN(elev) || elev < 2)
                                                    irradiance = 0;
                                                  else if (elev < 5)
                                                    irradiance =
                                                      adjustedDni *
                                                      Math.sin(
                                                        (elev * Math.PI) / 180
                                                      ) *
                                                      ((elev - 2) / 3);
                                                  else
                                                    irradiance =
                                                      adjustedDni *
                                                      Math.sin(
                                                        (elev * Math.PI) / 180
                                                      );

                                                  const power =
                                                    irradiance *
                                                    panelArea *
                                                    efficiency;
                                                  return `${irradiance.toFixed(
                                                    2
                                                  )} × ${panelArea} × ${efficiency} = ${power.toFixed(
                                                    2
                                                  )} W`;
                                                })()}
                                              </code>
                                            </div>

                                            {/* Step 4: Final (cap at 30W) */}
                                            <div>
                                              <code className="primary-font">
                                                Final:{" "}
                                                {(() => {
                                                  const elev = parseFloat(
                                                    solarData.month_elev
                                                  );
                                                  const monthlyDniRaw =
                                                    parseFloat(solarData.dni) *
                                                    parseFloat(
                                                      solarData.seasonal_factor
                                                    );
                                                  const adjustedDni = Math.min(
                                                    monthlyDniRaw,
                                                    1200.0
                                                  );
                                                  const panelArea = 0.2;
                                                  const efficiency = 0.15;

                                                  let irradiance = 0;
                                                  if (isNaN(elev) || elev < 2)
                                                    irradiance = 0;
                                                  else if (elev < 5)
                                                    irradiance =
                                                      adjustedDni *
                                                      Math.sin(
                                                        (elev * Math.PI) / 180
                                                      ) *
                                                      ((elev - 2) / 3);
                                                  else
                                                    irradiance =
                                                      adjustedDni *
                                                      Math.sin(
                                                        (elev * Math.PI) / 180
                                                      );

                                                  const power =
                                                    irradiance *
                                                    panelArea *
                                                    efficiency;
                                                  const finalPower = Math.min(
                                                    power,
                                                    30
                                                  );
                                                  return `min(${power.toFixed(
                                                    2
                                                  )}, 30) = ${finalPower.toFixed(
                                                    2
                                                  )} W`;
                                                })()}
                                              </code>
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <div>
                                              <code className="primary-font">
                                                Irradiance:{" "}
                                                {monthlyIrradiance
                                                  ? `max(0, ${solarData?.monthly_dni.toFixed(
                                                      1
                                                    )} × (${
                                                      solarData?.elev
                                                    }/90)) = ${monthlyIrradiance.toFixed(
                                                      1
                                                    )} W/m²`
                                                  : "--"}
                                              </code>
                                            </div>
                                            <div>
                                              <code className="primary-font">
                                                Power:{" "}
                                                {monthlyPower
                                                  ? `${monthlyIrradiance.toFixed(
                                                      1
                                                    )} × 0.2 × 0.15 = ${monthlyPower.toFixed(
                                                      2
                                                    )} W`
                                                  : "--"}
                                              </code>
                                            </div>
                                            <div>
                                              <code className="primary-font">
                                                Final:{" "}
                                                {monthlyFinalPower
                                                  ? `min(${monthlyPower.toFixed(
                                                      2
                                                    )}, 30) = ${monthlyFinalPower.toFixed(
                                                      2
                                                    )} W`
                                                  : "--"}
                                              </code>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-black fixed-bottom text-end footer-content">
        <h4 className="white-font d-inline-block fw-normal p-2 pe-5">
          Current Date & Time : {currentTime}
        </h4>
      </div>
    </>
  );
};
